<template>
  <div class="main-wrapper">
    <layoutheader />
    <breadcrumb-doctor title="Clinic settings" text="Doctor" text1="Clinic settings" />
    <div class="content">
      <div class="container">
        <div class="row">
          <doctorsidebar />
          <div class="col-md-7 col-lg-8 col-xl-9 adm-scope lh">

            <p class="adm-muted lh-intro">Your prescription letterhead for each clinic. These images are printed on every prescription you give at that clinic.</p>

            <div v-if="error" class="adm-alert adm-alert--error"><i class="fas fa-exclamation-circle"></i><div>{{ error }}</div></div>
            <div v-if="success" class="adm-alert adm-alert--info"><i class="fas fa-check-circle"></i><div>{{ success }}</div></div>

            <div v-if="!loadingClinics && !clinics.length" class="adm-card adm-empty">
              <i class="fas fa-hospital"></i>
              <strong>You are not assigned to a clinic yet</strong>
              Ask the YourDoc team to add you to your clinic; its settings will appear here.
            </div>

            <template v-else>
              <div v-if="clinics.length > 1" class="adm-tabs lh-tabs" role="tablist" aria-label="Clinic">
                <button v-for="c in clinics" :key="c.clinicid" type="button" class="adm-tab" role="tab"
                        :class="{ 'is-active': clinicid === c.clinicid }" :aria-selected="clinicid === c.clinicid" @click="selectClinic(c.clinicid)">
                  {{ c.clinicname }}
                </button>
              </div>

              <div class="lh-grid">
                <!-- Uploads -->
                <section class="adm-card">
                  <div class="adm-card__header">
                    <div>
                      <h3 class="adm-card__title">{{ clinic ? clinic.clinicname : "Clinic" }}</h3>
                      <p class="adm-card__hint">{{ clinic && clinic.clinicaddress ? clinic.clinicaddress : "PNG or JPG, up to 2 MB each." }}</p>
                    </div>
                  </div>
                  <div class="adm-card__body">
                    <div v-if="loadingSettings" class="adm-skeleton" style="height: 260px"></div>
                    <template v-else>
                      <div v-for="part in PARTS" :key="part.key" class="lh-part">
                        <div class="lh-part__head">
                          <div>
                            <span class="adm-strong">{{ part.label }}</span>
                            <span class="adm-person__meta">{{ part.hint }}</span>
                          </div>
                          <span v-if="pending[part.key]" class="adm-badge adm-badge--pending">Not saved</span>
                        </div>
                        <label class="lh-drop" :class="{ 'has-image': shown(part.key), 'is-over': over === part.key, [`lh-drop--${part.key}`]: true }"
                               @dragover.prevent="over = part.key" @dragleave="over = ''" @drop.prevent="onDrop(part.key, $event)">
                          <img v-if="shown(part.key)" :src="shown(part.key)" :alt="part.label" />
                          <span v-else class="lh-drop__empty"><i :class="part.icon"></i> Drop an image here or <u>choose a file</u></span>
                          <input type="file" accept="image/png,image/jpeg" class="lh-file" @change="onPick(part.key, $event)" />
                        </label>
                        <div class="lh-part__actions">
                          <button v-if="pending[part.key]" type="button" class="adm-btn adm-btn--sm adm-btn--ghost" @click="undo(part.key)">Undo</button>
                          <button v-else-if="current[part.key]" type="button" class="adm-btn adm-btn--sm adm-btn--ghost lh-remove"
                                  :disabled="saving" @click="removing = part">Remove</button>
                        </div>
                      </div>
                    </template>
                  </div>
                  <div class="adm-pagination lh-footer">
                    <span>{{ pendingCount ? `${pendingCount} change${pendingCount === 1 ? "" : "s"} not saved` : "All changes saved" }}</span>
                    <button type="button" class="adm-btn adm-btn--primary" :disabled="!pendingCount || saving" @click="save">
                      <span v-if="saving" class="adm-spinner"></span> Save letterhead
                    </button>
                  </div>
                </section>

                <!-- Live preview -->
                <section class="adm-card lh-preview">
                  <div class="adm-card__header">
                    <div>
                      <h3 class="adm-card__title">Preview</h3>
                      <p class="adm-card__hint">How a prescription will look (sample patient).</p>
                    </div>
                  </div>
                  <div class="lh-paper-wrap">
                    <div class="lh-paper" v-html="previewHtml"></div>
                  </div>
                </section>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <admin-modal v-if="removing" size="sm" :busy="saving" :title="`Remove ${removing.label.toLowerCase()}?`" @close="removing = null">
      <p class="lh-confirm">Prescriptions at this clinic will be printed without it. You can upload a new one any time.</p>
      <template #footer>
        <button type="button" class="adm-btn" :disabled="saving" @click="removing = null">Keep</button>
        <button type="button" class="adm-btn adm-btn--danger" :disabled="saving" @click="remove(removing)">
          <span v-if="saving" class="adm-spinner"></span> Remove
        </button>
      </template>
    </admin-modal>

    <doctorfooter />
  </div>
</template>

<script>
import axios from "axios";
import "../admin/admin.css";
import AdminModal from "../admin/components/AdminModal.vue";
import { errorMessage } from "../admin/admin-utils";
import { prescriptionHtml } from "@/services/prescription";

const MAX_BYTES = 2 * 1024 * 1024;
// key: our name; field: upload form field; settings: GET field; api: DELETE part
const PARTS = [
  { key: "header", field: "headerimage", settings: "prescrip_header_image", api: "header", label: "Header / logo",
    hint: "Printed across the top. A wide banner (about 1200 × 200 px) works best.", icon: "fas fa-image" },
  { key: "footer", field: "footerimage", settings: "prescrip_footer_image", api: "footer", label: "Footer",
    hint: "Printed at the bottom, e.g. clinic timings or address.", icon: "fas fa-grip-lines" },
  { key: "signature", field: "docsignature", settings: "docsignature", api: "signature", label: "Signature",
    hint: "Your signature on a white or transparent background.", icon: "fas fa-signature" },
];

export default {
  name: "doctor-clinic-settings",
  components: { AdminModal },
  data() {
    return {
      PARTS,
      clinics: [],
      loadingClinics: true,
      clinicid: "",
      doctor: null,
      current: { header: null, footer: null, signature: null }, // saved images (data URLs)
      pending: { header: null, footer: null, signature: null }, // { file, url } chosen but not saved
      loadingSettings: false,
      saving: false,
      removing: null,
      over: "",
      error: "",
      success: "",
    };
  },
  computed: {
    doctorId() {
      return sessionStorage.getItem("userdbid");
    },
    clinic() {
      return this.clinics.find(c => c.clinicid === this.clinicid) || null;
    },
    pendingCount() {
      return Object.values(this.pending).filter(Boolean).length;
    },
    previewHtml() {
      const d = this.doctor || {};
      const c = this.clinic || {};
      return prescriptionHtml({
        doctor: { name: d.doctorname || "Doctor", specialization: d.specialization, qualifications: d.qualifications, registration: d.registration_info },
        clinic: { name: c.clinicname, address: c.clinicaddress, phone: c.contactnumbers },
        patient: { id: 1024, name: "Sample Patient", age: "34", gender: "F" },
        appointment: { id: 5001, date: new Date().toISOString().slice(0, 10), reason: "Fever and sore throat for 3 days" },
        prescription: { date: new Date().toISOString(), note: "Acute pharyngitis.\nWarm saline gargles, plenty of fluids." },
        medications: [
          { medicinename: "Paracetamol", brandname: "Dolo 650", dosage: "Tablet, 650 mg", frequency: "1-1-1", duration: "3", note: "After food" },
          { medicinename: "Azithromycin", brandname: "Azee 500", dosage: "Tablet, 500 mg", frequency: "1-0-0", duration: "3", note: "Before food" },
        ],
        letterhead: { header_image: this.shown("header"), footer_image: this.shown("footer"), signature: this.shown("signature") },
      });
    },
  },
  methods: {
    shown(key) {
      return this.pending[key] ? this.pending[key].url : this.current[key];
    },
    accept(key, file) {
      this.error = "";
      if (!file) return;
      if (!/^image\/(png|jpe?g)$/.test(file.type)) {
        this.error = "Please choose a PNG or JPG image.";
        return;
      }
      if (file.size > MAX_BYTES) {
        this.error = "That image is larger than 2 MB. Please use a smaller one.";
        return;
      }
      this.undo(key);
      this.pending[key] = { file, url: URL.createObjectURL(file) };
    },
    onPick(key, event) {
      this.accept(key, event.target.files[0]);
      event.target.value = "";
    },
    onDrop(key, event) {
      this.over = "";
      this.accept(key, event.dataTransfer.files[0]);
    },
    undo(key) {
      if (this.pending[key]) URL.revokeObjectURL(this.pending[key].url);
      this.pending[key] = null;
    },
    async loadClinics() {
      try {
        const [clinics, doctor] = await Promise.all([
          axios.get(`/doctors/${this.doctorId}/clinicids`, { params: { details: true } }),
          axios.get(`/doctors/${this.doctorId}`).catch(() => ({ data: null })),
        ]);
        this.clinics = clinics.data || [];
        this.doctor = doctor.data;
        if (this.clinics.length) await this.selectClinic(this.clinics[0].clinicid);
      } catch (error) {
        this.error = errorMessage(error, "Could not load your clinics.");
      } finally {
        this.loadingClinics = false;
      }
    },
    async selectClinic(clinicid) {
      PARTS.forEach(p => this.undo(p.key));
      this.clinicid = clinicid;
      this.success = "";
      this.loadingSettings = true;
      try {
        const { data } = await axios.get(`/doctors/${this.doctorId}/doctorclinic-settings/${encodeURIComponent(clinicid)}`);
        const img = v => (v ? `data:image/png;base64,${v}` : null);
        this.current = { header: img(data.prescrip_header_image), footer: img(data.prescrip_footer_image), signature: img(data.docsignature) };
      } catch (error) {
        this.current = { header: null, footer: null, signature: null };
        this.error = errorMessage(error, "Could not load this clinic's letterhead.");
      } finally {
        this.loadingSettings = false;
      }
    },
    async save() {
      this.saving = true;
      this.error = "";
      try {
        const form = new FormData();
        PARTS.forEach(p => { if (this.pending[p.key]) form.append(p.field, this.pending[p.key].file); });
        await axios.post(`/doctors/${this.doctorId}/doctorclinic-settings/${encodeURIComponent(this.clinicid)}`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        PARTS.forEach(p => { if (this.pending[p.key]) this.current[p.key] = this.pending[p.key].url; this.pending[p.key] = null; });
        this.success = "Letterhead saved. New prescriptions at this clinic will use it.";
      } catch (error) {
        this.error = errorMessage(error, "Could not save the letterhead.");
      } finally {
        this.saving = false;
      }
    },
    async remove(part) {
      this.saving = true;
      this.error = "";
      try {
        await axios.delete(`/doctors/${this.doctorId}/doctorclinic-settings/${encodeURIComponent(this.clinicid)}/${part.api}`);
        this.current[part.key] = null;
        this.success = `${part.label} removed.`;
        this.removing = null;
      } catch (error) {
        this.error = errorMessage(error, `Could not remove the ${part.label.toLowerCase()}.`);
      } finally {
        this.saving = false;
      }
    },
  },
  created() {
    this.loadClinics();
  },
  beforeUnmount() {
    PARTS.forEach(p => this.undo(p.key));
  },
};
</script>

<style scoped>
.lh {
  font-size: 14px;
}

.lh-intro {
  margin: 0 0 14px;
  font-size: 13px;
}

.lh-tabs {
  margin-bottom: 14px;
}

.lh-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 16px;
  align-items: start;
}

.lh-part + .lh-part {
  margin-top: 18px;
  padding-top: 18px;
  border-top: 1px solid var(--adm-line-2);
}

.lh-part__head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 8px;
}

.lh-part__head .adm-person__meta {
  font-size: 12px;
}

.lh-drop {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 90px;
  border: 1.5px dashed #d0d5dd;
  border-radius: var(--adm-radius-sm);
  background: #fcfcfd;
  cursor: pointer;
  overflow: hidden;
  margin: 0;
  transition: border-color 0.15s, background 0.15s;
}

.lh-drop:hover,
.lh-drop.is-over {
  border-color: var(--adm-primary);
  background: var(--adm-primary-50);
}

.lh-drop.has-image {
  border-style: solid;
  background: #fff;
}

.lh-drop img {
  max-width: 100%;
  max-height: 120px;
  object-fit: contain;
  padding: 8px;
}

.lh-drop--signature img {
  max-height: 80px;
}

.lh-drop__empty {
  color: var(--adm-muted);
  font-size: 13px;
  padding: 12px;
  text-align: center;
}

.lh-drop__empty i {
  margin-right: 6px;
  color: var(--adm-faint);
}

.lh-file {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}

.lh-part__actions {
  display: flex;
  justify-content: flex-end;
  min-height: 8px;
}

.lh-remove {
  color: var(--adm-danger-700);
}

.lh-footer {
  border-top: 1px solid var(--adm-line-2);
}

.lh-preview {
  position: sticky;
  top: 90px;
}

.lh-paper-wrap {
  padding: 16px;
  background: #eef1f5;
  border-radius: 0 0 var(--adm-radius) var(--adm-radius);
  overflow: hidden;
}

/* An A4 sheet (10 mm margins around the 190 mm prescription) at half size;
   the negative margins give back the space the scaled-down page no longer uses. */
.lh-paper {
  width: 210mm;
  min-height: 297mm;
  background: #fff;
  padding: 10mm;
  box-shadow: 0 4px 16px rgba(16, 24, 40, 0.15);
  transform: scale(0.5);
  transform-origin: top left;
  margin-bottom: -148.5mm;
  margin-right: -105mm;
}

.lh-confirm {
  margin: 0;
}

@media (max-width: 1199.98px) {
  .lh-grid {
    grid-template-columns: 1fr;
  }

  .lh-preview {
    position: static;
  }
}
</style>
